import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Menu,
  MenuItem,
  Grid2,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { servicesUrl } from "../../utils/urls";
import EmptyListUi from "../../components/EmptyListUi";
import PageHeader from "../../components/page-layout/PageHeader";
import LoadingUI from "../../components/LoadingUI";
import { toast } from "react-hot-toast";

const ITEM_HEIGHT = 48;

const Services = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState({});
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  useEffect(() => {
    axiosInstance
      .get(servicesUrl)
      .then((servicesRes) => {
        const services = Array.isArray(servicesRes.data.data)
          ? servicesRes.data.data
          : [];
        const formatted = services.map((service) => ({
          id: service.id,
          name: service.name.trim(),
          printPreference: service.printPreference,
          isRequired: service.isRequired || false,
          isDefault: service.isDefault || false,
          description: service.description,
          types: Array.isArray(service.service_types)
            ? service.service_types.map((type) => ({
                id: type.id,
                name: type.name.trim(),
                icon: type.icon,
              }))
            : [],
        }));
        setRows(formatted);
      })
      .catch((err) => console.error("Failed to fetch services:", err))
      .finally(() => setIsLoading(false));
  }, []);

  const handleClick = (event, index) => {
    setAnchorEl((prev) => ({ ...prev, [index]: event.currentTarget }));
  };

  const handleClose = (index) => {
    setAnchorEl((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const handleEditClick = (index) => {
    navigate(`/services/edit/${rows[index].id}`);
    handleClose(index);
  };

  // Open confirmation dialog
  const handleDeleteClick = (index) => {
    setDeleteIndex(index);
    setDeleteDialogOpen(true);
    handleClose(index);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    const index = deleteIndex;
    const service = rows[index];
    try {
      await axiosInstance.delete(`${servicesUrl}/${service.id}`);
      setRows((prev) => prev.filter((s) => s.id !== service.id));
      toast.success("Service deleted successfully");
    } catch (err) {
      console.error("Failed to delete service:", err);
      toast.error("Failed to delete service");
    } finally {
      setDeleteDialogOpen(false);
      setDeleteIndex(null);
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <Stack spacing={2}>
      <PageHeader
        showBack={true}
        title="Services"
        lead="Manage your available services and their types. Add, edit, or remove services as needed."
        buttonTitle="Add Service"
        buttonOnClick={() => navigate("/services/add")}
      />

      <Box sx={{ width: "100%", display: "grid", rowGap: 4 }}>
        {isLoading ? (
          <LoadingUI text="Loading services..." />
        ) : rows.length === 0 ? (
          <EmptyListUi
            text="No services found."
            buttonLabel="Add Service"
            onClick={() => navigate("/services/add")}
          />
        ) : (
          rows.map((service, index) => {
            let prefLabel =
              service.printPreference === true
                ? "Per Service"
                : service.printPreference === false
                ? "Per Page"
                : "Not Set";

            return (
              <Card
                key={service.id || index}
                component={Paper}
                elevation={2}
                sx={{ borderRadius: 4 }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h5">{service.name}</Typography>
                      <Chip
                        label={prefLabel}
                        size="small"
                        color="default"
                        sx={{ ml: 1 }}
                      />
                      {/* Display Required chip if true */}
                      {service.isRequired && (
                        <Chip
                          label="Required"
                          size="small"
                          color="primary"
                        />
                      )}
                      {/* Display Default chip if true */}
                      {service.isDefault && (
                        <Chip
                          label="Default"
                          size="small"
                          color="primary"
                        />
                      )}
                    </Box>
                    <Button
                      sx={{
                        width: 32,
                        height: 32,
                        minWidth: "unset",
                        borderRadius: 6,
                        color: "black",
                      }}
                      onClick={(e) => handleClick(e, index)}
                      size="small"
                    >
                      <MoreVertIcon color="primary" fontSize="small" />
                    </Button>
                    <Menu
                      anchorEl={anchorEl[index] || null}
                      open={Boolean(anchorEl[index])}
                      onClose={() => handleClose(index)}
                      MenuListProps={{ "aria-labelledby": "long-button" }}
                      slotProps={{
                        paper: {
                          style: {
                            maxHeight: ITEM_HEIGHT * 4.5,
                            width: "16ch",
                          },
                        },
                      }}
                      anchorOrigin={{ vertical: "top", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <MenuItem onClick={() => handleEditClick(index)}>
                        Edit
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleDeleteClick(index)}
                        sx={{ color: "error.main" }}
                      >
                        Delete
                      </MenuItem>
                    </Menu>
                  </Box>
                  <Typography variant="subtitle2" mb={2} color="text.secondary">
                    {service.description || "No description provided."}
                  </Typography>
                  <Grid2 container spacing={2}>
                    {service.types.length > 0 ? (
                      service.types.map((type) => (
                        <Paper
                          key={type.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 2,
                            gap: 1,
                            borderRadius: 2,
                          }}
                          elevation={1}
                        >
                          {type.icon && (
                            <img
                              src={type.icon}
                              alt={type.name}
                              width={32}
                              height={32}
                              style={{ borderRadius: 5 }}
                            />
                          )}
                          <Typography variant="h6" fontSize={16} pl={1}>
                            {type.name}
                          </Typography>
                        </Paper>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ pl: 2 }}
                      >
                        No service types available.
                      </Typography>
                    )}
                  </Grid2>
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Service</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <b>
              {deleteIndex !== null && rows[deleteIndex]
                ? rows[deleteIndex].name
                : ""}
            </b>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button onClick={handleCancelDelete} size="small">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="outlined"
            size="small"
          >
            Sure
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default Services;
